import { useLocation, useNavigate } from 'react-router-dom';
import { IEnvelopeOverlayLocationState } from '@common/Routing/types';

import {
  EnvelopeAllowedActionsButton,
  EnvelopeAllowedActionsName,
  BrowserStorage,
  Modal,
} from '@const/consts'
import { ACTION_REQUIRED_COUNTS_UPDATED_EVENT } from '@const/events';
import { Path } from '@const/paths';
import { catchThunkError, displayBasicNotification, displayConfirmNotification, displayErrorNotification } from '@utils/utils';
import { getEnvelopeTaskForDecline } from '@utils/envelopeTasks';
import { getSharedLink } from '@utils/sharedLinks';

import EnvelopesService from '@services/envelopes'
import SharedService from '@services/shared'

import { IHandleAllowedAction } from '@views/envelopes/types';
import { IConfirmNotificationOptions } from '@utils/types';
import { useLoadEnvelopes } from '@hooks/envelopes/useLoadEnvelopes';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../redux';
import { setModal } from '@app/store/modules/modal';
import {
  getEnvelope,
  getPreview,
  setCurrentBoxOguid,
  setDraftEnvelope,
} from '@store/modules/envelopes';
import { getDraftEnvelopeFromEnvelope } from '@views/envelopes/utils';
import { ICurrentEnvelope } from '@store/modules/envelopes/types';

export const useAllowedActions = () => {
  const { t } = useTranslation(['envelope'])
  const navigate = useNavigate()
  const location = useLocation()
  const { loadCurrentEnvelope, loadEnvelopes } = useLoadEnvelopes()
  const { pathname, search } = location
  const dispatch = useAppDispatch()
  const personalBoxOguid = useAppSelector((state) => state.user.profile.boxOguid);
  const direction = useAppSelector((state) => state.envelopes.envelopes.direction);
  const currentEnvelope = useAppSelector((state) => state.envelopes.currentEnvelope);

  const openDeclineModal = (envelope: ICurrentEnvelope | null | undefined): void => {
    if (!getEnvelopeTaskForDecline(envelope)) {
      displayBasicNotification({
        content: t('notification:taskNotFound'),
        title: t('notification:errorTitle'),
        type: 'error',
      });
      return;
    }

    dispatch(setModal(Modal.DECLINE));
  };

  const handleClickAllowedActions = ({
    option,
    oguid,
    subject,
    isCurrentEnvelope = false,
    boxOguid,
    direction: actionDirection,
    isQualifiedSignature
  }: IHandleAllowedAction): void => {
    const { name } = option
    const envelopeDirection = actionDirection ?? direction

    const openValues: string[] = [
      EnvelopeAllowedActionsName.OPEN,
      EnvelopeAllowedActionsButton.VIEW
    ]

    const signedActionsValues: string[] = [
      EnvelopeAllowedActionsButton.APPROVE,
      EnvelopeAllowedActionsButton.SIGN,
    ]

    const isOpen = openValues.includes(name);
    const isSignedAction = signedActionsValues.includes(name);
    const isStopProcess = name === EnvelopeAllowedActionsName.STOP_PROCESS;
    const isDeclineProcess = name === EnvelopeAllowedActionsName.DECLINE;
    const isUseAsTemplate = name === EnvelopeAllowedActionsName.USE_AS_TEMPLATE;

    if (isOpen && oguid) {
      const url = pathname + search
      localStorage.setItem(BrowserStorage.LAST_URL, url)
      let path = Path.ENVELOPE.replace(':oguid', oguid)

      if (personalBoxOguid !== boxOguid) {
        path = Path.ENVELOPE_BUSINESS.replace(':boxOguid', boxOguid).replace(':oguid', oguid);
      }

      const envelopePath = envelopeDirection
        ? `${path}?direction=${envelopeDirection}`
        : path
      const state: IEnvelopeOverlayLocationState = {
        backgroundLocation: location,
      }

      navigate(envelopePath, { state })
    }

    if (isDeclineProcess && oguid) {
      if (isCurrentEnvelope) {
        openDeclineModal(currentEnvelope)
      } else {
        dispatch(getEnvelope({ direction: envelopeDirection, oguid, boxOguid }))
          .unwrap()
          .then((envelope) => {
            if (envelope) openDeclineModal(envelope)
          })
          .catch(catchThunkError)
      }
    }

    if (isUseAsTemplate && oguid) {
      dispatch(getEnvelope({ direction: envelopeDirection, oguid, boxOguid }))
        .unwrap()
        .then((envelope) => {
          if (!envelope) return

          const draftEnvelope = getDraftEnvelopeFromEnvelope(envelope)

          dispatch(setCurrentBoxOguid(boxOguid))
          dispatch(setDraftEnvelope(draftEnvelope))
          draftEnvelope.files.forEach((fileUuid) => {
            dispatch(getPreview({ fileUuid })).unwrap().catch(catchThunkError)
          })
          navigate(Path.ENVELOPE_NEW, { state: { useDraftTemplate: true } })
        })
        .catch(catchThunkError)
    }

    if (isStopProcess && oguid) {
      const notify: IConfirmNotificationOptions = {
        title: t('envelope:notification:stopProcessTitle'),
        content: `${t(
          'envelope:notification:stopProcessContent'
        )} "${subject}"?`,
        theme: 'danger',
        onSubmit: () => {
          EnvelopesService.postEnvelopeFlowStop(oguid)
            .then(() => {
              isCurrentEnvelope ? loadCurrentEnvelope() : loadEnvelopes();
            })
            .catch(displayErrorNotification);
        },
      };

      displayConfirmNotification(notify);
    }

    if (isSignedAction && oguid) {
      if (isQualifiedSignature) {
        dispatch(setModal(Modal.ENVELOPE_SIGN))
      } else {
        SharedService.getSharedOguid(oguid)
          .then(({ data }) => {
            const sharedWindow = window.open(getSharedLink(data), '_blank')

            if (!sharedWindow) return

            window.addEventListener('focus', () => {
              window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT))
              isCurrentEnvelope ? loadCurrentEnvelope() : loadEnvelopes()
            }, { once: true })
          })
          .catch(displayErrorNotification)
      }
    }
  }

  return {
    handleClickAllowedActions
  }
}
